// src/oura/ui/Tiles.tsx
//
// One tile anatomy everywhere: a label with its icon, a number, and one line
// under it pinned to the bottom. A tile is a link, a button that opens
// something, or plain; on merchant screens every tile opens something.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "../lib/cn";
import { FOCUS_RING, SURFACE, TYPE } from "./tokens";
import { PanelIcon, type PanelIconSpec } from "./PanelIcon";

/**
 * Equal-height tiles, 1 column on a phone. Three go straight to one row from
 * 768px: a 2-column step left the third tile alone beside a tile-sized gap.
 * Four step through 2 columns from 640px to one row from 1024px.
 */
export function Tiles({
    cols,
    children,
    className,
    compactOnPhone,
}: {
    /** 2 is opt in: a pair side by side from 768px. */
    cols: 2 | 3 | 4;
    children: ReactNode;
    className?: string;
    /** Opt in: tighter gaps under 640px, for tiles that pass `compactOnPhone`. */
    compactOnPhone?: boolean;
}) {
    return (
        <div
            className={cn(
                "grid grid-cols-1 items-stretch gap-4",
                cols === 2 ? "md:grid-cols-2" : cols === 3 ? "md:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4",
                compactOnPhone && "max-sm:gap-2",
                className,
            )}
        >
            {children}
        </div>
    );
}

/**
 * "23" at tile size, then "of 50" (or a unit such as "avg position") smaller,
 * in ink4.
 *
 * THE SPACE IS A REAL CHARACTER, always. It used to be a 4px left margin, with
 * a real space only where a caller remembered to opt in, so the gap was there
 * on screen and absent everywhere text is actually read: a screen reader said
 * "36of 50 questions", and so did anything copied out of the page (#4025
 * FE-12). Three of the nine call sites had not opted in.
 *
 * There is no opt-out. A margin that looks like a space but is not one is the
 * whole bug, so the only way to keep it from coming back is for the component
 * to have one behaviour.
 */
export function OfValue({ n, of, unit }: { n: ReactNode; of?: number | string; unit?: string }) {
    const tail = [of !== undefined ? `of ${of}` : null, unit].filter(Boolean).join(" ");
    return (
        <>
            {n}
            {tail && <span className="text-[17px] font-medium leading-6 tracking-normal text-gray-400">{" "}{tail}</span>}
        </>
    );
}

export interface TileProps {
    label: ReactNode;
    icon?: PanelIconSpec;
    /** A number, or <OfValue>. Rendered at 28px. */
    value: ReactNode;
    /** The one line under the value; may hold a rose late note. */
    sub?: ReactNode;
    /**
     * Trailing element on the sub-line, such as a pill that names what the
     * tile opens. The tile itself is the control, so this must not be one.
     */
    action?: ReactNode;
    to?: string;
    onClick?: () => void;
    /** The value is on its way: a placeholder bar, never a 0. */
    loading?: boolean;
    /** Accessible name for a linked or clickable tile, when the label alone does not say where it goes. */
    ariaLabel?: string;
    /** Opt in: an id on the tile, for a screen that must find it again (to put focus back on it). */
    id?: string;
    /**
     * Opt in: under 640px the tile is one row, the label and its line on the
     * left and the number on the right, so three stacked tiles do not push
     * the page's first list below the fold.
     */
    compactOnPhone?: boolean;
    className?: string;
}

export function Tile({ label, icon, value, sub, action, to, onClick, loading, ariaLabel, id, compactOnPhone, className }: TileProps) {
    const c = Boolean(compactOnPhone);
    const body = (
        <>
            <span className={cn(TYPE.lab, "flex items-center gap-2", c && "max-sm:col-start-1 max-sm:row-start-1")}>
                {icon && <PanelIcon icon={icon.icon} area={icon.area} size={16} />}
                {label}
            </span>
            <span
                className={cn(
                    TYPE.num,
                    "mt-1 block text-gray-900",
                    c && "max-sm:col-start-2 max-sm:row-span-2 max-sm:row-start-1 max-sm:mt-0 max-sm:text-right max-sm:text-[22px] max-sm:leading-7",
                )}
            >
                {loading ? <span className="inline-block h-7 w-20 animate-pulse rounded-md bg-gray-100 align-middle" /> : value}
            </span>
            {(sub || action) && (
                <span className={cn(TYPE.tc, "flex items-end justify-between gap-2 pt-1.5 text-gray-500", c && "max-sm:col-start-1 max-sm:row-start-2 max-sm:mt-0 max-sm:pt-0.5")}>
                    <span className="min-w-0">{sub}</span>
                    {action}
                </span>
            )}
        </>
    );
    const shell = cn(
        SURFACE.card,
        // No fixed height and no push to the foot (2 Nov 2026): the note sits right
        // under its number, so a tile never opens a gap between the two.
        "flex flex-col p-4 text-left",
        c && "max-sm:grid max-sm:min-h-0 max-sm:grid-cols-[minmax(0,1fr)_auto] max-sm:items-center max-sm:gap-x-3 max-sm:py-3",
        className,
    );
    const interactive = cn(shell, "transition-colors hover:bg-gray-50", FOCUS_RING);

    if (to) {
        return (
            <Link id={id} to={to} aria-label={ariaLabel} className={interactive}>
                {body}
            </Link>
        );
    }
    if (onClick) {
        return (
            <button id={id} type="button" onClick={onClick} aria-label={ariaLabel} className={interactive}>
                {body}
            </button>
        );
    }
    return <div id={id} className={shell}>{body}</div>;
}
